/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@farmlands/shadcn-ui";
import { Search } from "lucide-react";
import { SyntheticEvent, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";

import { usePermission } from "../../../hooks/usePermission";
import Analytics from "../../../services/analytics";
import TestIds from "../../../testIds";
import { PARAM_KEYS, PRODUCTS_ROUTES } from "../../products/constants";

const SearchBar = () => {
  const canViewPreviousOrders = usePermission("FarmlandsProShop_PreviousOrdersView").allowed;
  const [searchText, setSearchText] = useState<string>("");
  const [selected, setSelected] = useState("0");
  const navigate = useNavigate();
  const search = (event?: SyntheticEvent) => {
    event?.preventDefault();

    if (searchText) {
      Analytics.push({
        dataLayer: {
          event: "search_term",
          searchTerm: searchText,
        },
      });
    }

    navigate({
      pathname: PRODUCTS_ROUTES.BASE,
      search: createSearchParams({
        [PARAM_KEYS.search]: searchText ?? "",
        [PARAM_KEYS.tab]: selected,
      }).toString(),
    });
  };

  return (
    <form
      onSubmit={search}
      className={`flex w-full flex-col items-start gap-4 lg:flex-row lg:items-center`}
    >
      <InputGroup size="lg" className="w-full lg:flex-1">
        <InputGroupInput
          placeholder="Search Farmlands products"
          value={searchText}
          maxLength={200}
          className="text-base md:text-base"
          data-testid={TestIds.components.searchWidget.searchBar}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <InputGroupAddon align="inline-start">
          <InputGroupText data-testid={TestIds.components.searchWidget.searchIcon}>
            <Search className="size-4 text-neutral-500" />
          </InputGroupText>
        </InputGroupAddon>
      </InputGroup>

      <RadioGroup
        value={selected}
        onValueChange={setSelected}
        name="search-radio-buttons-group"
        className="flex flex-row gap-6"
        data-testid={TestIds.components.searchWidget.radioButtonGroup}
      >
        <div
          className="flex items-center gap-2"
          data-testid={TestIds.components.searchWidget.radioButtonGroupDataId("all")}
        >
          <RadioGroupItem value="0" id="search-bar-option-all" />
          <Label htmlFor="search-bar-option-all">All products</Label>
        </div>
        {canViewPreviousOrders && (
          <div
            className="flex items-center gap-2"
            data-testid={TestIds.components.searchWidget.radioButtonGroupDataId(
              "previously-ordered"
            )}
          >
            <RadioGroupItem value="1" id="search-bar-option-previously-ordered" />
            <Label htmlFor="search-bar-option-previously-ordered">Previously ordered</Label>
          </div>
        )}
      </RadioGroup>
    </form>
  );
};

export default SearchBar;
